import { useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useCatalogApi } from '@/webapi/use-catalog-api';
import { AccountContext } from '@/features/account-context';
import {
  PageKind,
  StorePage,
} from '@/features/editor/context/use-device-navigation';
import { AiFocusedEditorBridge, AiFocusedEditorState } from './types';

export function toPageKind(p: StorePage): PageKind {
  if (p.url?.match(/^https?:\/\/[^/]+(\/)?$/)) {
    return PageKind.HOMEPAGE;
  }
  if (p.url?.includes(`/products/`)) {
    return PageKind.PRODUCT;
  }
  if (p.url?.includes(`/collections/`)) {
    return PageKind.COLLECTION;
  }
  if (p.url?.includes(`/cart`)) {
    return PageKind.CART;
  }

  return PageKind.CUSTOM;
}

/**
 * Handles catalog search logic for the AI editor preview.
 */
export function useAiCatalogSearch(
  state: AiFocusedEditorState,
  _bridge: AiFocusedEditorBridge,
) {
  const { account } = useContext(AccountContext);
  const { searchCatalog } = useCatalogApi();

  useAsyncEffect(async () => {
    state.isSearching.value = true;
    try {
      const q = state.searchQuery.value;
      const res = await searchCatalog(q, 5, account?.store?.domain);

      let pages = res?.pages || [];

      if (q.includes(`https://`) || q.includes(`www.`) || q.includes(`.`)) {
        if (!pages.find((p) => p.url === q)) {
          const url =
            q.startsWith(`http`) || q.startsWith(`www`) ? q : `https://${q}`;
          pages = [
            {
              url,
              caption: `Custom Page: ${q}`,
              kind: PageKind.CUSTOM,
              rank: 0,
            },
            ...pages,
          ];
        }
      }

      pages.forEach((p) => {
        p.kind = toPageKind(p);
      });

      state.searchResults.value = pages;
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error(`Failed to search catalog`, e);
    } finally {
      state.isSearching.value = false;
    }
  }, [state.searchQuery.value]);
}
